import React from "react";
import { Box, Card, CardContent, Chip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { qualificationLabel } from "./displayHelpers";
import PlayerIdentityLabel from "./PlayerIdentityLabel";
import PoolMatchRow from "./PoolMatchRow";
import { surfaceSx } from "./resultatsTheme";
import type { PlayerDirectory, Pool, QualificationStatus } from "./types";
import styles from "./Resultats.module.css";

interface PoolCardProps {
  pool: Pool;
  players: PlayerDirectory;
  highlightedLicence?: string | null;
  onSelectLicence?: (licence: string) => void;
}

function qualificationChipColor(
  status: QualificationStatus | undefined
): "success" | "default" | "warning" | undefined {
  switch (status) {
    case "QUALIFIED":
      return "success";
    case "ELIMINATED":
      return "default";
    case "PENDING":
      return "warning";
    default:
      return undefined;
  }
}

export default function PoolCard({
  pool,
  players,
  highlightedLicence = null,
  onSelectLicence,
}: PoolCardProps): React.ReactElement {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Card
      className={styles.poolCard}
      elevation={0}
      sx={surfaceSx(theme)}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: 2.5 } }}>
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: 800,
            fontSize: "1.15rem",
            letterSpacing: "-0.02em",
            mb: 1.5,
          }}
        >
          {pool.label}
        </Typography>

        <Box
          sx={{
            borderRadius: 2,
            px: 1,
            py: 0.5,
            mb: 0.5,
            bgcolor: isDark
              ? alpha(theme.palette.common.white, 0.04)
              : alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Typography
            component="p"
            sx={{
              m: 0,
              mb: 0.75,
              px: 0.5,
              pt: 0.5,
              fontSize: "0.72rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "text.secondary",
            }}
          >
            Classement
          </Typography>
          <table className={styles.standingsTable}>
            <thead>
              <tr>
                <th scope="col">
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700 }}
                  >
                    #
                  </Typography>
                </th>
                <th scope="col">
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700 }}
                  >
                    Joueur
                  </Typography>
                </th>
                <th scope="col" style={{ textAlign: "right" }}>
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700 }}
                  >
                    V / D
                  </Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {pool.standings.map((standing, index) => {
                const qualLabel = qualificationLabel(standing.qualification);
                const chipColor = qualificationChipColor(
                  standing.qualification
                );
                const isHighlighted =
                  Boolean(standing.licence) &&
                  standing.licence === highlightedLicence;
                const isQualified = standing.qualification === "QUALIFIED";
                return (
                  <tr
                    key={`${standing.licence ?? "u"}-${index}`}
                    className={`${
                      isQualified ? styles.standingQualified : ""
                    }${isHighlighted ? ` ${styles.standingHighlighted}` : ""}`}
                    onClick={() => {
                      if (standing.licence && onSelectLicence) {
                        onSelectLicence(standing.licence);
                      }
                    }}
                    style={{
                      cursor:
                        standing.licence && onSelectLicence
                          ? "pointer"
                          : undefined,
                      ...(isQualified
                        ? {
                            backgroundColor: alpha(
                              theme.palette.success.main,
                              isDark ? 0.2 : 0.12
                            ),
                          }
                        : {}),
                    }}
                  >
                    <td className={styles.rank}>
                      {standing.rank != null ? standing.rank : "—"}
                    </td>
                    <td>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flexWrap: "wrap",
                          minWidth: 0,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                          <PlayerIdentityLabel
                            licence={standing.licence}
                            directory={players}
                            emptyLabel="Joueur inconnu"
                            winner={standing.rank === 1}
                            dense
                          />
                        </Box>
                        {qualLabel && chipColor && (
                          <Chip
                            size="small"
                            color={chipColor}
                            variant={
                              standing.qualification === "ELIMINATED"
                                ? "outlined"
                                : "filled"
                            }
                            label={qualLabel}
                            className={styles.qualChip}
                            sx={{
                              height: 22,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              flexShrink: 0,
                              "& .MuiChip-label": { px: 0.7 },
                            }}
                          />
                        )}
                      </Box>
                    </td>
                    <td className={styles.record}>
                      {standing.wins}&nbsp;V&nbsp;/&nbsp;{standing.losses}
                      &nbsp;D
                    </td>
                  </tr>
                );
              })}
              {pool.standings.length === 0 && (
                <tr>
                  <td colSpan={3}>
                    <Typography variant="body2" color="text.secondary">
                      Classement non disponible
                    </Typography>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        <Box className={styles.matchesBlock}>
          <Typography
            className={styles.matchesBlockTitle}
            color="text.secondary"
            component="p"
          >
            Rencontres
          </Typography>
          {pool.matches.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Aucune rencontre
            </Typography>
          ) : (
            pool.matches.map((game) => (
              <PoolMatchRow key={game.id} game={game} players={players} />
            ))
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
